/**
 * 三方渠道通用判断方法
 */

import { storage } from '@/utils/storageOperateApi';
import { LOCAL_STORAGE_KEY } from '@/constants';
import { getUrlParam } from '@/utils/api';
import { isBuild } from '@/apis/host';
import { ElMessageBox } from 'element-plus';
import { customLink } from '@/utils/api';
import store from '@/store';
import router from '@/router';

// 动态获取合作信息的函数
export const getLatestCooperationInfo = () => {
  return storage.get(LOCAL_STORAGE_KEY.COOPERATION, true) || {};
};

// 获取URL参数
export const getUrlParams = () => {
  return getUrlParam();
};

// 获取进门用户渠道列表
// f48991ad-c322-4bff-93d4-f7f6ae8d5bbd 深圳进门财经科技股份有限公司
// 64da1bf6-0b28-4146-9468-61c603f0c567   深圳进门信息技术有限公司(定3)
export const getJmUserChannels = () => {
  return isBuild
    ? [
        '64da1bf6-0b28-4146-9468-61c603f0c567',
        'f48991ad-c322-4bff-93d4-f7f6ae8d5bbd',
        '8aee60a1-a35b-4247-b188-ae4aa1cf7a3e'
      ]
    : [];
};

// 判断是否是公司内部（判断浏览器或者缓存中userChannel/comein_channel
export const checkIsJmCompany = (userChannel) => {
  const jmUserChannels = getJmUserChannels();
  return jmUserChannels.includes(userChannel);
};

// 合作渠道判断
/**
 * 判断是否为第三方模式（根据链接和缓存信息）
 *
 * 一、链接带userChannel（重新登录场景）：
 *   1. 判断链接中的userChannel是否与comein_channel匹配，且链接中没有userSecretKey
 *   2. 判断公司内部userChannel，且链接中的brmChannel与brmChannel参数匹配
 *
 * 二、链接不带userChannel（取本地缓存场景）：
 *   1. 判断缓存中的userChannel是否与comein_channel匹配
 *   2. 判断公司内部userChannel，且缓存中的brmChannel与brmChannel参数匹配
 */
export function isThirdPartyMode({ brmChannel, comein_channel, noComeinChannel = false }) {
  const localChannel = getLatestCooperationInfo();
  const urlParams = getUrlParams();

  /**
   * 针对IRM系统适配没有comein_channel的情况
   * 如果链接带source，则直接判断source是否与brmChannel匹配
   * 如果noComeinChannel为true，则判断缓存中的brmChannel是否与brmChannel匹配
   */
  const source = urlParams?.source;
  if (source) {
    return source === brmChannel;
  }

  if (noComeinChannel && localChannel?.brmChannel === brmChannel) {
    return true;
  }

  const userChannel = urlParams?.userChannel || urlParams?.comein_channel;
  if (userChannel) {
    const isJmCompany = checkIsJmCompany(userChannel);
    return (
      (userChannel === comein_channel && !urlParams?.userSecretKey) ||
      (isJmCompany && urlParams?.brmChannel === brmChannel)
    );
  }
  const isJmCompany = checkIsJmCompany(localChannel?.userChannel);

  if (localChannel?.userChannel && comein_channel) {
    return localChannel?.userChannel === comein_channel;
  }
  if (isJmCompany && localChannel?.brmChannel) {
    return isJmCompany && localChannel?.brmChannel === brmChannel;
  }
  return false;
}

//通用渠道判断

// 一、链接带userChannel （重新登录）
// 1、公司内部userChannel且brmChannel为normal

// 二、链接不带userChannel （取缓存）
// 1、公司内部userChannel且brmChannel为normal

export function isGeneralMode({ brmChannel }) {
  const localChannel = getLatestCooperationInfo();
  const urlParams = getUrlParams();

  const source = urlParams?.source;
  if (source) {
    return source === brmChannel;
  }

  const userChannel = urlParams?.userChannel || urlParams?.comein_channel;
  if (userChannel) {
    const isJmCompany = checkIsJmCompany(userChannel);
    return isJmCompany && urlParams?.brmChannel === brmChannel;
  }

  const isJmCompany = checkIsJmCompany(localChannel.userChannel);
  return isJmCompany && localChannel?.brmChannel === brmChannel;
}

/**
 * Bridge 基类
 */
export class BridgeBase {
  constructor(brmChannel, showMenu = false, redirectUri = null, empowerAlertFn = null) {
    this.comein_channel = brmChannel;
    this.thirdparty = brmChannel;
    this.showMenu = showMenu;
    this.empowerAlertFn = empowerAlertFn;
    this.redirectUri = redirectUri;
  }

  // 授权提示
  empowerAlert() {
    if (this.empowerAlertFn) {
      return this.empowerAlertFn();
    }
    return ElMessageBox.alert('联系公司BRM系统管理员升级企业版后可查看', '提示', {
      confirmButtonText: '我知道了',
      type: 'warning'
    });
  }

  // 路演列表点击路演事件跳转路演厅事件
  goRoadshowDetail(params) {
    store.dispatch('enterRoadshowLiveAction', {
      ...params,
      callback: (path) => {
        customLink(path);
      }
    });
  }

  // 纪要点击ai帮你看/跳往纪要
  goRoadshowPage({ id } = {}) {
    if (id) {
      store.dispatch('enterRoadshowLiveAction', {
        id,
        callback: (path) => {
          customLink(path);
        }
      });
    }
    return {
      disabled: true,
      action: () => this.empowerAlert()
    };
  }

  // 点击行业
  clickIndustry() {
    this.empowerAlert();
  }

  // 点击个股
  clickStock() {
    this.empowerAlert();
  }

  // 点击调研
  clickResearch(payload) {
    const path = router.resolve(payload);
    customLink(path.href);
  }

  // 跳转问答详情页
  goQuestionDetail(payload) {
    const path = router.resolve(payload);
    customLink(path.href);
  }

  // 跳转纪要详情页
  goSummaryDetail(payload) {
    const path = router.resolve(payload);
    customLink(path.href);
  }

  // 跳转研报详情页
  goReportDetail(payload) {
    const path = router.resolve(payload);
    customLink(path.href);
  }

  // 日程 - 调研详情
  clickResearchDetail(payload) {
    const path = router.resolve(payload);
    customLink(path.href);
  }

  // 日程 - 策略会详情
  clickStrategyDetail(payload) {
    const path = router.resolve(payload);
    customLink(path.href);
  }

  // 日程 - 其他
  clickScheduleDetail(payload) {
    const path = router.resolve(payload);
    customLink(path.href);
  }
}
